import React, { useEffect } from 'react';
import { DroppableProvided } from 'react-beautiful-dnd';
import { useVirtualizer } from '@tanstack/react-virtual';
import useStudioClipCompleteStyles from '../StudioClip/States/Complete/completeStyles';
import { StudioClipProps } from '../StudioClip/StudioClip';
import { STUDIO_CLIP_HEIGHT } from '../StudioClip/StudioClipCard';
import Layout from '../BaseLayout/components';

export type ClipRendererProps = {
  clip: StudioClipProps['clip'];
  index: number;
  studioClipCompleteClasses: ReturnType<typeof useStudioClipCompleteStyles>;
};
export interface StudioClipListProps {
  clips: Array<StudioClipProps['clip']>;
  clipRenderer: (props: ClipRendererProps) => React.ReactNode;
  autoloadDisabled?: boolean;
  autoloadFn?: (offset: number) => void;
  droppableProvided?: DroppableProvided;
}

/**
 * Component used for rendering a list of Clips. Features include list virtualization and autoload
 * capabilities. For optimal performance, ensure components returned by the `clipRenderer` prop are
 * sufficiently memoized as this is called very frequently onScroll.
 *
 * It is recommended to wrap this in a <NoSsr/> tag because of the use of window/document to identify the scroll element.
 *
 * @example
 * <StudioClipList
 *    clips={clips}
 *    autoloadFn={() => {}}
 *    autoloadDisabled={loading || noMoreClips}
 *    clipRenderer={({clip, ...props}) => (
 *        <StudioClipWrapper>
 *          <StudioClip
 *            clip={formattedClip}
 *          />
 *        </StudioClipWrapper>
 *    )}
 * />
 */
const StudioClipList = (props: StudioClipListProps) => {
  const {
    clips,
    clipRenderer,
    autoloadDisabled,
    autoloadFn,
    droppableProvided,
  } = props;
  const studioClipCompleteClasses = useStudioClipCompleteStyles();

  const scrollElement =
    document.getElementById(Layout.MAIN_CONTENT_ID) ||
    document.getElementById('page-shareable') ||
    null;
  const listRef = React.useRef<HTMLDivElement | null>(null);

  const rowVirtualizer = useVirtualizer({
    count: clips.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => STUDIO_CLIP_HEIGHT,
    overscan: 20,
    gap: 8,
    scrollMargin: 260,
  });

  // Handles autoloading/infinite scroll.
  useEffect(() => {
    if (autoloadDisabled) return;

    // Exists if last item in "view"
    const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

    if (!lastItem) {
      return;
    }

    if (
      !autoloadDisabled &&
      typeof autoloadFn === 'function' &&
      lastItem.index >= clips.length - 1
    ) {
      autoloadFn(lastItem.index);
    }
  }, [
    autoloadDisabled,
    autoloadFn,
    clips.length,
    rowVirtualizer.getVirtualItems(),
  ]);

  return (
    <div ref={listRef} style={{ position: 'relative' }}>
      <div
        id={'clips'}
        role="grid"
        aria-label="Clips"
        aria-rowcount={clips.length}
        style={{
          position: 'relative',
          height: rowVirtualizer.getTotalSize(),
        }}
      >
        {rowVirtualizer.getVirtualItems().map(virtualRow => {
          const clip = clips[virtualRow.index];
          if (listRef && droppableProvided) {
            droppableProvided.innerRef(listRef.current);
          }
          return (
            <div
              role="row"
              aria-rowindex={virtualRow.index + 1}
              key={String(virtualRow.key)}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                transform: `translateY(${
                  virtualRow.start - rowVirtualizer.options.scrollMargin
                }px)`,
                width: '100%',
              }}
            >
              <div role="gridcell">
                {clipRenderer({
                  clip,
                  index: virtualRow.index,
                  studioClipCompleteClasses,
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StudioClipList;
